import axios, { 
  AxiosRequestConfig, 
  AxiosResponse, 
  AxiosError,
  InternalAxiosRequestConfig 
} from 'axios';
import { getAuthData, performLogout, refreshAccessToken } from './authService';
import { universalAuthService } from './universalAuthService';
import { API_URL } from '../constants/api';

// Флаг для предотвращения множественных refresh запросов
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Класс для работы с API
class ApiClient {
  private static instance: ApiClient;

  private constructor() {
    // Инициализация перехватчиков
    this.setupInterceptors();
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  // Конфигурация axios с перехватчиками
  private setupInterceptors(): void {
    // Перехватчик запросов для добавления токена
    axios.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const { token } = getAuthData();
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    // Перехватчик ответов для обработки ошибок авторизации с поддержкой refresh token
    axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          console.log('401 error in apiClient, attempting token refresh...');

          // Определяем тип пользователя по наличию данных в localStorage
          const userTypes: ('user' | 'field' | 'referee')[] = ['field', 'referee', 'user'];
          let activeUserType: 'user' | 'field' | 'referee' = 'user';

          for (const type of userTypes) {
            const { token, refreshToken } = universalAuthService.getStoredData(type);
            if (token && refreshToken) {
              activeUserType = type;
              break;
            }
          }

          console.log(`[apiClient] Active user type: ${activeUserType}`);

          // Попытка обновить токен для активного типа пользователя
          const newTokens = await refreshAccessToken(activeUserType);
          if (newTokens) {
            console.log(`[apiClient] Token refresh successful for ${activeUserType}, retrying request...`);

            // Обновляем токены в localStorage
            universalAuthService.updateTokens(activeUserType, newTokens.token, newTokens.refreshToken);

            // Обновляем заголовок Authorization в оригинальном запросе
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newTokens.token}`;
            }

            // Повторяем оригинальный запрос
            return axios(originalRequest);
          } else {
            console.log(`[apiClient] Token refresh failed for ${activeUserType}, performing logout...`);
            // Если обновление не удалось - делаем logout
            performLogout();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Refresh token теперь поддерживается через UnifiedAuthService

  // Методы для выполнения запросов
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await axios.get<T>(`${API_URL}${url}`, config);
    return response.data;
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await axios.post<T>(`${API_URL}${url}`, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await axios.put<T>(`${API_URL}${url}`, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await axios.delete<T>(`${API_URL}${url}`, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await axios.patch<T>(`${API_URL}${url}`, data, config);
    return response.data;
  }

  public async postFormData<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response = await axios.post<T>(`${API_URL}${url}`, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  public async putFormData<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response = await axios.put<T>(`${API_URL}${url}`, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

// Экспортируем инстанс
export const apiClient = ApiClient.getInstance();
export default apiClient; 